import { useEffect, useState, useRef } from "react";
import { vpAxios, money } from "../api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Upload, Image as ImageIcon } from "lucide-react";

export default function ProdutosPage() {
  const [rows, setRows] = useState([]);
  const [cats, setCats] = useState([]);
  const [catsPadrao, setCatsPadrao] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const load = async () => {
    const [rp, rc, rcp] = await Promise.all([
      vpAxios.get("/vaipede/produtos"),
      vpAxios.get("/vaipede/categorias"),
      vpAxios.get("/vaipede/categorias-padrao"),
    ]);
    setRows(rp.data); setCats(rc.data); setCatsPadrao(rcp.data);
  };
  useEffect(() => { load(); }, []);

  const salvar = async (data) => {
    try {
      if (edit) await vpAxios.put(`/vaipede/produtos/${edit.id}`, data);
      else await vpAxios.post("/vaipede/produtos", data);
      toast.success("Salvo!"); setOpen(false); setEdit(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  const excluir = async (id) => {
    if (!window.confirm("Excluir produto?")) return;
    await vpAxios.delete(`/vaipede/produtos/${id}`); load();
  };

  return (
    <div data-testid="vaipede-produtos">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-black">Produtos</h1>
          <p className="text-sm text-neutral-500">Cardápio da lanchonete</p>
        </div>
        <button onClick={() => { setEdit(null); setOpen(true); }} className="px-4 py-2.5 rounded-xl bg-[#EA1D2C] text-white font-bold text-sm flex items-center gap-2" data-testid="vaipede-btn-add-prod">
          <Plus className="w-4 h-4"/> Novo produto
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-neutral-200 overflow-hidden">
        {rows.length === 0 && <p className="text-sm text-neutral-500 text-center py-10">Nenhum produto cadastrado.</p>}
        <div className="divide-y divide-neutral-100">
          {rows.map(p => (
            <div key={p.id} className="p-4 flex items-center gap-3">
              {p.foto_url ? (
                <img src={p.foto_url} alt="" className="w-14 h-14 rounded-xl object-cover border border-neutral-200"/>
              ) : (
                <div className="w-14 h-14 rounded-xl bg-neutral-100 grid place-items-center text-neutral-300">
                  <ImageIcon className="w-5 h-5"/>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="font-bold truncate">{p.nome}</div>
                <div className="text-xs text-neutral-500">{p.categoria || "—"}</div>
              </div>
              <div className="text-right">
                <div className="font-black text-[#EA1D2C]">{money(p.preco)}</div>
                <span className={`text-xs ${p.ativo === false ? "text-neutral-400" : "text-emerald-600"}`}>{p.ativo === false ? "Inativo" : "Ativo"}</span>
              </div>
              <button onClick={() => { setEdit(p); setOpen(true); }} className="p-2 text-neutral-500 hover:text-neutral-900"><Pencil className="w-4 h-4"/></button>
              <button onClick={() => excluir(p.id)} className="p-2 text-neutral-500 hover:text-red-600"><Trash2 className="w-4 h-4"/></button>
            </div>
          ))}
        </div>
      </div>

      {open && <ProdutoModal onClose={() => { setOpen(false); setEdit(null); }} onSave={salvar} initial={edit} categorias={cats} categoriasPadrao={catsPadrao} onReloadCats={() => vpAxios.get("/vaipede/categorias").then(r => setCats(r.data))}/>}
    </div>
  );
}

function ProdutoModal({ onClose, onSave, initial, categorias, categoriasPadrao, onReloadCats }) {
  const fileRef = useRef(null);
  const [f, setF] = useState({
    nome: initial?.nome || "",
    descricao: initial?.descricao || "",
    preco: initial?.preco ?? "",
    categoria: initial?.categoria || (categorias[0]?.nome ?? ""),
    foto_url: initial?.foto_url || "",
    ativo: initial?.ativo !== false,
  });
  const [uploading, setUploading] = useState(false);

  // Merge: já cadastradas + padrão (sem duplicar), ordenadas por prioridade
  const nomesCadastrados = new Set((categorias || []).map(c => (c.nome || "").toLowerCase()));
  const merged = [
    ...(categorias || []).map(c => ({ nome: c.nome, icone: c.icone || "🍽️", ordem: c.ordem ?? 500, existente: true })),
    ...(categoriasPadrao || []).filter(c => !nomesCadastrados.has(c.nome.toLowerCase()))
      .map(c => ({ nome: c.nome, icone: c.icone, ordem: c.ordem, existente: false })),
  ].sort((a, b) => (a.ordem - b.ordem) || a.nome.localeCompare(b.nome));

  const escolherFoto = () => fileRef.current?.click();

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2_500_000) { toast.error("Foto muito grande (máx. 2.5MB)"); return; }
    setUploading(true);
    try {
      const dataUrl = await new Promise((res, rej) => {
        const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file);
      });
      const { data } = await vpAxios.post("/vaipede/uploads/imagem", { data_url: dataUrl });
      setF(prev => ({ ...prev, foto_url: data.url }));
      toast.success("Foto enviada");
    } catch (err) { toast.error(err?.response?.data?.detail || "Falha no upload"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const escolherCategoria = async (nome) => {
    setF(prev => ({ ...prev, categoria: nome }));
    // Se ainda não existe na loja, cria automaticamente
    if (!nomesCadastrados.has(nome.toLowerCase())) {
      try {
        await vpAxios.post("/vaipede/categorias", { nome });
        onReloadCats();
      } catch (e) { /* ignora se já existir */ }
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-md p-5 max-h-[95vh] overflow-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-black text-lg">{initial ? "Editar" : "Novo"} produto</h2>
          <button onClick={onClose}><X className="w-5 h-5"/></button>
        </div>
        <div className="space-y-2">
          {/* Foto */}
          <div className="flex items-center gap-3">
            {f.foto_url ? (
              <img src={f.foto_url} alt="" className="w-20 h-20 rounded-xl object-cover border border-neutral-200"/>
            ) : (
              <div className="w-20 h-20 rounded-xl bg-neutral-100 grid place-items-center text-neutral-400"><ImageIcon className="w-6 h-6"/></div>
            )}
            <div className="flex-1">
              <input type="file" accept="image/*" ref={fileRef} onChange={onFileChange} className="hidden" data-testid="vaipede-prod-foto-input"/>
              <button onClick={escolherFoto} disabled={uploading} className="w-full py-2 rounded-xl bg-neutral-100 text-sm font-bold flex items-center justify-center gap-2" data-testid="vaipede-prod-foto-btn">
                <Upload className="w-4 h-4"/> {uploading ? "Enviando..." : (f.foto_url ? "Trocar foto" : "Adicionar foto")}
              </button>
              {f.foto_url && <button onClick={() => setF({...f, foto_url: ""})} className="text-xs text-red-500 mt-1">Remover foto</button>}
            </div>
          </div>

          <input value={f.nome} onChange={e => setF({...f, nome: e.target.value})} placeholder="Nome" className="w-full px-3 py-2.5 rounded-xl border border-neutral-200 text-sm" data-testid="vaipede-prod-nome"/>
          <textarea value={f.descricao} onChange={e => setF({...f, descricao: e.target.value})} placeholder="Descrição" rows={2} className="w-full px-3 py-2.5 rounded-xl border border-neutral-200 text-sm resize-none"/>
          <div className="grid grid-cols-2 gap-2">
            <input type="number" step="0.01" value={f.preco} onChange={e => setF({...f, preco: e.target.value})} placeholder="Preço" className="px-3 py-2.5 rounded-xl border border-neutral-200 text-sm" data-testid="vaipede-prod-preco"/>
            <select value={f.categoria} onChange={e => escolherCategoria(e.target.value)} className="px-3 py-2.5 rounded-xl border border-neutral-200 text-sm" data-testid="vaipede-prod-categoria">
              <option value="">Selecione a categoria</option>
              {merged.map(c => (
                <option key={c.nome} value={c.nome}>{c.icone} {c.nome}</option>
              ))}
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={f.ativo} onChange={e => setF({...f, ativo: e.target.checked})}/>
            Ativo (visível no cardápio)
          </label>
        </div>
        <button onClick={() => onSave({...f, preco: parseFloat(f.preco || 0)})} className="w-full mt-4 py-3 rounded-xl bg-[#EA1D2C] text-white font-bold" data-testid="vaipede-btn-save-prod">
          Salvar
        </button>
      </div>
    </div>
  );
}
